import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

interface ImageUploadOptions {
  bucket: string;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  generateThumbnail?: boolean;
}

interface ImageUploadResult {
  url: string;
  thumbnailUrl?: string;
  metadata: {
    width: number;
    height: number;
    size: number;
    type: string;
  };
}

export const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const compressImage = useCallback((file: File, maxWidth: number = 1920, maxHeight: number = 1920, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Fallback to original if compression fails
          }
        }, file.type, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  }, []);

  const generateThumbnail = useCallback((file: File, size: number = 200): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();

      img.onload = () => {
        canvas.width = size;
        canvas.height = size;

        // Center crop to square
        const { width, height } = img;
        const cropSize = Math.min(width, height);
        const x = (width - cropSize) / 2;
        const y = (height - cropSize) / 2;

        ctx.drawImage(img, x, y, cropSize, cropSize, 0, 0, size, size);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const thumbnailFile = new File([blob], `thumb_${file.name}`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(thumbnailFile);
          } else {
            resolve(file); // Fallback
          }
        }, 'image/jpeg', 0.8);
      };

      img.src = URL.createObjectURL(file);
    });
  }, []);

  const getImageMetadata = useCallback((file: File): Promise<{ width: number; height: number; size: number; type: string }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve({
          width: img.width,
          height: img.height,
          size: file.size,
          type: file.type,
        });
      };
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const uploadImage = async (
    file: File,
    options: ImageUploadOptions
  ): Promise<ImageUploadResult | null> => {
    try {
      setUploading(true);
      setUploadProgress(0);

      // Handle SVG files differently as they are vector-based
      const isSvg = file.type === 'image/svg+xml';
      
      // Get image metadata (skip for SVG as they don't have pixel dimensions)
      const metadata = isSvg 
        ? { width: 0, height: 0, size: file.size, type: file.type }
        : await getImageMetadata(file);
      
      // Compress main image if needed (skip compression for SVG)
      let mainFile = file;
      if (!isSvg && (options.maxWidth || options.maxHeight)) {
        mainFile = await compressImage(
          file, 
          options.maxWidth, 
          options.maxHeight, 
          options.quality
        );
      }

      // Generate filename with timestamp
      const timestamp = Date.now();
      const extension = file.name.split('.').pop();
      const baseFileName = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9.-]/g, '_');
      const mainFileName = `${timestamp}-${baseFileName}.${extension}`;

      setUploadProgress(30);

      // Upload main image
      const { data: mainData, error: mainError } = await supabase.storage
        .from(options.bucket)
        .upload(mainFileName, mainFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (mainError) throw mainError;

      setUploadProgress(60);

      // Get public URL for main image
      const { data: { publicUrl } } = supabase.storage
        .from(options.bucket)
        .getPublicUrl(mainFileName);

      let thumbnailUrl: string | undefined;

      // Generate and upload thumbnail if requested (skip for SVG)
      if (options.generateThumbnail && !isSvg) {
        const thumbnailFile = await generateThumbnail(file);
        const thumbnailFileName = `thumb_${mainFileName}`;

        const { data: thumbData, error: thumbError } = await supabase.storage
          .from(options.bucket)
          .upload(thumbnailFileName, thumbnailFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (!thumbError) {
          const { data: { publicUrl: thumbUrl } } = supabase.storage
            .from(options.bucket)
            .getPublicUrl(thumbnailFileName);
          thumbnailUrl = thumbUrl;
        }
      }

      setUploadProgress(100);

      return {
        url: publicUrl,
        thumbnailUrl,
        metadata
      };

    } catch (error: any) {
      console.error('[IMAGE-UPLOAD] Error uploading image:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload image. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteImage = async (bucket: string, path: string): Promise<boolean> => {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) throw error;

      // Also try to delete thumbnail if it exists
      const thumbnailPath = path.replace(/^([^/]+\/)/, '$1thumb_');
      await supabase.storage
        .from(bucket)
        .remove([thumbnailPath]);

      return true;
    } catch (error: any) {
      console.error('[IMAGE-UPLOAD] Error deleting image:', error);
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete image.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    uploading,
    uploadProgress,
    uploadImage,
    deleteImage,
    compressImage,
    generateThumbnail,
    getImageMetadata
  };
};